// Keyboard movement for the listbox popovers (DropdownSelect, SearchableSelect).
// Pure so the arrow / Home / End / type-ahead rules can be tested directly.

export interface NavOption {
  label: string
  disabled?: boolean
}

/** First enabled index at or after `from` walking by `step`, or -1. No wrap. */
function scan(options: NavOption[], from: number, step: 1 | -1): number {
  for (let i = from; i >= 0 && i < options.length; i += step) {
    if (!options[i].disabled) return i
  }
  return -1
}

/**
 * The option a key moves the active descendant to, or null when the key is not
 * a navigation key. `current` is -1 when nothing is active yet.
 */
export function listboxMove(options: NavOption[], current: number, key: string): number | null {
  if (options.length === 0) return null
  switch (key) {
    case 'ArrowDown': {
      if (current < 0) return scan(options, 0, 1)
      const next = scan(options, current + 1, 1)
      return next === -1 ? current : next
    }
    case 'ArrowUp': {
      if (current < 0) return scan(options, options.length - 1, -1)
      const prev = scan(options, current - 1, -1)
      return prev === -1 ? current : prev
    }
    case 'Home':
      return scan(options, 0, 1)
    case 'End':
      return scan(options, options.length - 1, -1)
    default:
      return null
  }
}

/** Index of the option to start on when the list opens: the selection, else the first enabled. */
export function openingIndex(options: NavOption[], selectedIndex: number): number {
  if (selectedIndex >= 0 && !options[selectedIndex]?.disabled) return selectedIndex
  return scan(options, 0, 1)
}

/**
 * Type-ahead: the next enabled option after `current` whose label starts with
 * the typed character (case-insensitive), wrapping round. -1 when none match.
 */
export function typeAheadIndex(options: NavOption[], current: number, char: string): number {
  const needle = char.toLowerCase()
  const n = options.length
  // Q6-FD6: a multi-character buffer ("mini") keeps refining the current
  // match, so it searches from the current option itself; a single key
  // moves on to the next match.
  const first = needle.length > 1 ? 0 : 1
  const base = needle.length > 1 ? Math.max(current, 0) : current
  for (let step = first; step <= n; step++) {
    const i = (base + step + n) % n
    const o = options[i]
    if (!o.disabled && o.label.toLowerCase().startsWith(needle)) return i
  }
  return -1
}
